import { useCallback, useEffect, useState } from "react"

export interface UseDisclosureProps<T = unknown> {
	isOpen?: boolean
	defaultIsOpen?: boolean
	defaultValue?: T
	onClose?(): void
	onOpen?(value?: T): void
}

export type UseDisclosureReturn<T = unknown> = {
	isOpen: boolean
	open: (value?: T) => void
	close: () => void
	toggle: (value?: T) => void
	value: T | undefined
}

/**
 * `useDisclosure` is a custom hook used to help handle common open, close, or toggle scenarios.
 * It can be used to control feedback component such as `Modal`.
 */
export function useDisclosure<T = unknown>({
	onOpen: onOpenProp,
	onClose: onCloseProp,
	isOpen: isOpenProp,
	defaultIsOpen,
	defaultValue,
}: UseDisclosureProps<T> = {}): UseDisclosureReturn<T> {
	const [value, setValue] = useState<T | undefined>(defaultValue)
	const [isOpenState, setIsOpen] = useState(defaultIsOpen || false)

	const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenState
	const isControlled = isOpenProp !== undefined

	const close = useCallback(() => {
		if (!isControlled) {
			setIsOpen(false)
		}
		onCloseProp?.()
		setValue(defaultValue)
	}, [defaultValue, isControlled, onCloseProp])

	const open = useCallback(
		(value?: T) => {
			if (!isControlled) {
				setIsOpen(true)
			}
			const isDomEvent =
				value !== null &&
				typeof value === "object" &&
				("nativeEvent" in value ||
					("target" in value && "preventDefault" in value))
			const nextValue =
				value !== undefined && !isDomEvent ? value : defaultValue
			onOpenProp?.(nextValue)
			setValue(nextValue)
		},
		[defaultValue, isControlled, onOpenProp],
	)

	const toggle = useCallback(
		(value?: T) => {
			if (isOpen) {
				close()
			} else {
				open(value)
			}
		},
		[isOpen, open, close],
	)

	useEffect(() => {
		if (!isOpen) {
			setValue(defaultValue)
		}
	}, [defaultValue, isOpen])

	return {
		isOpen,
		open,
		close,
		toggle,
		value,
	}
}
